import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ActividadForm } from '../../components/ActividadForm.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { FwaApiError } from '../../lib/api.js';
import { useActividad, useUpdateActividad } from '../../lib/queries/actividades.js';

export function ActividadesEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: actividadResp, isLoading, refetch } = useActividad(id);
  const mutation = useUpdateActividad();
  const [serverError, setServerError] = useState<string>('');
  const [showConflictModal, setShowConflictModal] = useState(false);

  if (isLoading) return <div className="text-center py-8 text-gray-600">Cargando…</div>;
  if (!actividadResp || !id) {
    return (
      <Card>
        <CardBody>
          <p className="text-gray-600">Actividad no encontrada.</p>
          <Link to="/actividades">
            <Button variant="ghost" className="mt-4">
              ← Regresar al listado
            </Button>
          </Link>
        </CardBody>
      </Card>
    );
  }

  const actividad = actividadResp.data.actividad;
  const etag = actividadResp.etag ?? actividad.version_id;

  return (
    <div className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-semibold text-gray-900">Editar actividad</h1>
      <p className="text-sm text-gray-500">
        Versión actual: <span className="font-mono text-xs">{actividad.version_id}</span>
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Datos editables</CardTitle>
        </CardHeader>
        <CardBody>
          <ActividadForm
            mode="edit"
            initialValues={{
              tipo: actividad.tipo,
              nombre: actividad.nombre,
              lugar: actividad.lugar,
              fecha_actividad: actividad.fecha_actividad,
              participantes: actividad.participantes,
              horas: actividad.horas,
              evidencias_urls: actividad.evidencias_urls ?? undefined,
              fotos: actividad.fotos ?? undefined,
            }}
            onSubmit={(payload) => {
              setServerError('');
              mutation.mutate(
                { id, payload, etag },
                {
                  onSuccess: (res) => {
                    if ('queuedOffline' in res) {
                      navigate('/outbox');
                      return;
                    }
                    navigate(`/actividades/${id}`);
                  },
                  onError: (err) => {
                    if (err instanceof FwaApiError) {
                      if (err.status === 412) {
                        setShowConflictModal(true);
                        return;
                      }
                      setServerError(err.problem.detail ?? err.problem.title);
                    } else {
                      setServerError(err.message);
                    }
                  },
                },
              );
            }}
            isSubmitting={mutation.isPending}
            serverError={serverError}
          />
        </CardBody>
      </Card>

      {showConflictModal && (
        <dialog
          open
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-none w-full h-full"
          aria-labelledby="conflict-title-act"
        >
          <Card className="max-w-md mx-4">
            <CardHeader>
              <CardTitle>
                <span id="conflict-title-act">Conflicto de versión</span>
              </CardTitle>
            </CardHeader>
            <CardBody>
              <p>
                Otra persona editó esta actividad mientras tú estabas viendo. Recarga para ver los
                cambios más recientes y vuelve a aplicar tus cambios.
              </p>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowConflictModal(false);
                    void refetch();
                  }}
                >
                  Recargar y re-intentar
                </Button>
                <Button variant="ghost" onClick={() => navigate(`/actividades/${id}`)}>
                  Descartar cambios
                </Button>
              </div>
            </CardBody>
          </Card>
        </dialog>
      )}

      <Link to={`/actividades/${id}`}>
        <Button variant="ghost">← Cancelar edición</Button>
      </Link>
    </div>
  );
}
